import { TypographyT5, TypographyT6 } from '@picsart/design-system/foundation/typography';

import {
  Button,
  ButtonElementTypes,
  ButtonSizeXL,
  ButtonSkinAbsoluteWhite,
  ButtonSkinStatusSuccess,
  ButtonVariants,
} from '@picsart/design-system/components/Button';
import { IconCopy } from '@picsart/design-system/foundation/icons/IconCopy';
import cls from 'classnames';
import Breadcrumb from 'components/Breadcrumb';
import { useMemo, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';

import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { useTranslator } from '@picsart/rc/services/localization';
import { Text } from '@picsart/design-system/components/Text';

import getColorForGradientBackground from 'utils/colors/getColorForGradientBackground';
import getTextColorForBackground from 'utils/colors/getTextColorForBackground';
import { getTextColorByCMSData } from 'utils/colors/getTextColorByCMSData';
import getRelatedColors from 'utils/colors/getRelatedColors';

import usePageDataStore from 'stores/pageDataStore';
import ColorCarousel from 'components/Colors/ColorsCarousel/ColorsCarousel';
import useStyles from './styles';
import { ColorBannerProps } from './types';

import TitleWithDescription from '../TitleWithDescription';

function ColorBanner({
  className,
  title,
  description,
  colorBannerButton,
  leftTextColor,
  rightTextColor,
}: ColorBannerProps) {
  const {
    pageData: { data },
    colorPages,
    colorData: { hex, category },
  } = usePageDataStore(store => store);
  const relatedColors = useMemo(
    () => getRelatedColors(colorPages, category, hex),
    [category, colorPages, hex],
  );
  const ctaTitle = colorBannerButton?.text || '';
  const ctaLink = colorBannerButton?.url || '';

  const rightSideColor =
    rightTextColor === 'auto'
      ? getTextColorForBackground(hex)
      : getTextColorByCMSData(rightTextColor);
  const leftSideColor =
    leftTextColor === 'auto'
      ? getColorForGradientBackground(hex)
      : getTextColorByCMSData(leftTextColor);

  const translate = useTranslator();
  const classes = useStyles({ color: hex, leftSideColor });
  const [isActiveButton, setIsActiveButton] = useState(false);

  const onClick = () => {
    setIsActiveButton(true);
    setTimeout(() => {
      setIsActiveButton(false);
    }, 1000);
  };

  return (
    <div className={cls(classes.root, className)}>
      <div className={classes.horizontalContainer}>
        <div className={classes.textContainer}>
          <div className={classes.breadcrumb}>
            <Breadcrumb className={classes.breadcrumb} />
          </div>
          <TitleWithDescription title={title} description={description} className={classes.title} />
          <Button
            className={classes.cta}
            tag={ButtonElementTypes.Link}
            variant={ButtonVariants.Filled}
            skin={ButtonSkinAbsoluteWhite}
            size={ButtonSizeXL}
            href={ctaLink}
          >
            {translate(ctaTitle)}
          </Button>
        </div>
        <div className={cls('box-shadow', classes.infoContainer)}>
          <div className={classes.info}>
            <div className={cls('flex-centered', classes.infoText)}>
              <Text
                className={classes.colorName}
                size={TypographyT6}
                weight={FontWeights.semibold}
                color={rightSideColor}
              >
                {data.name}
              </Text>
              <div className={classes.copyContainer}>
                <Text color={rightSideColor} size={TypographyT5}>
                  {hex}
                </Text>
                <CopyToClipboard text={hex}>
                  <Button
                    skin={isActiveButton ? ButtonSkinStatusSuccess : ButtonSkinAbsoluteWhite}
                    onClick={onClick}
                    className={classes.copyButton}
                    centerIcon={IconCopy}
                  />
                </CopyToClipboard>
              </div>
            </div>
          </div>
          <ColorCarousel colors={relatedColors} />
        </div>
      </div>
    </div>
  );
}

export default ColorBanner;
